import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  applyActionCode,
  checkActionCode,
  confirmPasswordReset,
  verifyPasswordResetCode,
} from 'firebase/auth';
import { auth } from '../../config/firebase';

const AuthActionPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [mode, setMode] = useState<string | null>(null);
  const [status, setStatus] = useState<
    'verifying' | 'success' | 'error' | 'reset-form'
  >('verifying');
  const [errorMessage, setErrorMessage] = useState('');

  // Password reset specific states
  const [email, setEmail] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [resetLoading, setResetLoading] = useState(false);

  useEffect(() => {
    const actionMode = searchParams.get('mode');
    const actionCode = searchParams.get('oobCode');

    setMode(actionMode);

    if (!actionCode || !actionMode) {
      setStatus('error');
      setErrorMessage(
        'Invalid verification link - missing required parameters'
      );
      return;
    }

    // Handle different modes
    if (actionMode === 'verifyEmail') {
      verifyEmail(actionCode);
    } else if (actionMode === 'resetPassword') {
      verifyPasswordReset(actionCode);
    } else {
      setStatus('error');
      setErrorMessage('Unknown action type');
    }
  }, [searchParams]);

  const verifyEmail = async (actionCode: string) => {
    try {
      // First, check if the action code is valid
      const info = await checkActionCode(auth, actionCode);
      console.log('Action code info:', info);

      // Apply the email verification code
      await applyActionCode(auth, actionCode);

      console.log('Email verification successful!');
      setStatus('success');

      // Clear all auth-related session flags to ensure clean state
      sessionStorage.removeItem('allow_unverified_setup');
      sessionStorage.removeItem('sms_verification_pending');
      sessionStorage.removeItem('skip_email_check');
      console.log('[EmailVerification] Cleared all auth session flags');

      // Sign out the user to ensure clean state for first login
      // This prevents issues with SMS 2FA flow and auto-redirects
      try {
        if (auth.currentUser) {
          console.log(
            '[EmailVerification] Signing out user for clean login flow'
          );
          await auth.signOut();
        }
      } catch (e) {
        console.error('[EmailVerification] Error signing out:', e);
        // Continue even if sign out fails
      }
    } catch (error: any) {
      console.error('Error during verification:', error);

      setStatus('error');

      if (error?.code === 'auth/invalid-action-code') {
        setErrorMessage(
          'This verification link has expired or has already been used.'
        );
      } else if (error?.code === 'auth/expired-action-code') {
        setErrorMessage(
          'This verification link has expired. Please log in and request a new verification email from your dashboard.'
        );
      } else {
        setErrorMessage(
          `Unable to verify email: ${error?.message || 'Unknown error'}`
        );
      }
    }
  };

  const verifyPasswordReset = async (actionCode: string) => {
    try {
      // Verify the password reset code and get the user's email
      const userEmail = await verifyPasswordResetCode(auth, actionCode);
      setEmail(userEmail);
      setStatus('reset-form');
    } catch (error: any) {
      console.error('Error verifying reset code:', error);

      setStatus('error');
      if (error?.code === 'auth/expired-action-code') {
        setErrorMessage(
          'This password reset link has expired. Please request a new one.'
        );
      } else if (error?.code === 'auth/invalid-action-code') {
        setErrorMessage(
          'This password reset link is invalid or has already been used.'
        );
      } else {
        setErrorMessage(
          'Failed to verify password reset link. Please try again.'
        );
      }
    }
  };

  const validatePassword = (password: string): string => {
    if (!password) return 'Password is required';
    if (password.length < 8) return 'Password must be at least 8 characters';
    if (password.length > 128)
      return 'Password must be less than 128 characters';
    if (!/[a-z]/.test(password))
      return 'Password must contain at least one lowercase letter';
    if (!/[A-Z]/.test(password))
      return 'Password must contain at least one uppercase letter';
    if (!/[0-9]/.test(password))
      return 'Password must contain at least one number';
    if (!/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password))
      return 'Password must contain at least one special character';
    return '';
  };

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMessage('');

    // Validate passwords
    const passwordError = validatePassword(newPassword);
    if (passwordError) {
      setErrorMessage(passwordError);
      return;
    }

    if (newPassword !== confirmPassword) {
      setErrorMessage('Passwords do not match');
      return;
    }

    const actionCode = searchParams.get('oobCode');
    if (!actionCode) {
      setErrorMessage('Invalid reset link');
      return;
    }

    setResetLoading(true);

    try {
      await confirmPasswordReset(auth, actionCode, newPassword);
      setStatus('success');
    } catch (error: any) {
      console.error('Error resetting password:', error);

      if (error?.code === 'auth/expired-action-code') {
        setErrorMessage(
          'This password reset link has expired. Please request a new one.'
        );
      } else if (error?.code === 'auth/invalid-action-code') {
        setErrorMessage(
          'This password reset link is invalid or has already been used.'
        );
      } else if (error?.code === 'auth/weak-password') {
        setErrorMessage(
          'Password is too weak. Please use a stronger password.'
        );
      } else {
        setErrorMessage('Failed to reset password. Please try again.');
      }
      setResetLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full">
        {/* Verifying State */}
        {status === 'verifying' && (
          <div className="text-center">
            <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-blue-600 mx-auto mb-8"></div>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              {mode === 'resetPassword'
                ? 'Verifying reset link...'
                : 'Verifying your email...'}
            </h2>
            <p className="text-gray-600">Please wait a moment</p>
          </div>
        )}

        {/* Password Reset Form */}
        {status === 'reset-form' && (
          <div className="bg-white rounded-xl shadow-lg p-8">
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg
                  className="w-8 h-8 text-blue-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z"
                  />
                </svg>
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">
                Reset Password
              </h2>
              <p className="text-gray-600">Enter your new password for</p>
              <p className="text-sm font-medium text-gray-900 mt-1">{email}</p>
            </div>

            {errorMessage && (
              <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-red-700 text-sm">{errorMessage}</p>
              </div>
            )}

            <form onSubmit={handleResetPassword} className="space-y-4">
              {/* New Password */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  New Password
                </label>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    required
                    disabled={resetLoading}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="••••••••"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                  >
                    {showPassword ? (
                      <svg
                        className="w-5 h-5"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21"
                        />
                      </svg>
                    ) : (
                      <svg
                        className="w-5 h-5"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                        />
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                        />
                      </svg>
                    )}
                  </button>
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  Must be 8+ characters with uppercase, lowercase, number, and
                  special character
                </p>
              </div>

              {/* Confirm Password */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Confirm New Password
                </label>
                <div className="relative">
                  <input
                    type={showConfirmPassword ? 'text' : 'password'}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                    disabled={resetLoading}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="••••••••"
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                  >
                    {showConfirmPassword ? (
                      <svg
                        className="w-5 h-5"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21"
                        />
                      </svg>
                    ) : (
                      <svg
                        className="w-5 h-5"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                        />
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                        />
                      </svg>
                    )}
                  </button>
                </div>
              </div>

              <button
                type="submit"
                disabled={resetLoading}
                className="w-full bg-blue-600 text-white py-3 rounded-lg font-semibold hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition mt-6"
              >
                {resetLoading ? 'Resetting Password...' : 'Reset Password'}
              </button>
            </form>

            <div className="mt-6 text-center">
              <button
                onClick={() => navigate('/login')}
                className="text-sm text-gray-600 hover:text-gray-900"
              >
                ← Back to Login
              </button>
            </div>
          </div>
        )}

        {/* Success State */}
        {status === 'success' && (
          <div className="bg-white rounded-xl shadow-lg p-8 text-center">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <svg
                className="w-8 h-8 text-green-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M5 13l4 4L19 7"
                />
              </svg>
            </div>

            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              {mode === 'resetPassword' ? 'Password Reset!' : 'Email Verified!'}
            </h2>

            <p className="text-gray-600 mb-6">
              {mode === 'resetPassword'
                ? 'Your password has been successfully reset. You can now log in with your new password.'
                : 'Your email has been successfully verified. You can now log in to your account and access all features.'}
            </p>

            <button
              onClick={() =>
                navigate('/login', {
                  state: {
                    message:
                      mode === 'resetPassword'
                        ? 'Password reset successful! Please log in with your new password.'
                        : 'Email verified! Please log in to continue.',
                    email: searchParams.get('email') || undefined,
                  },
                })
              }
              className="w-full bg-blue-600 text-white py-4 px-6 rounded-lg font-semibold hover:bg-blue-700 transition text-lg mb-3"
            >
              Continue to Login
            </button>

            <button
              onClick={() => navigate('/')}
              className="w-full bg-gray-100 text-gray-700 py-3 px-6 rounded-lg font-medium hover:bg-gray-200 transition"
            >
              Back to Home
            </button>
          </div>
        )}

        {/* Error State */}
        {status === 'error' && (
          <div className="bg-white rounded-xl shadow-lg p-8 text-center">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <svg
                className="w-8 h-8 text-red-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </div>

            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              {mode === 'resetPassword'
                ? 'Reset Failed'
                : 'Verification Failed'}
            </h2>

            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
              <p className="text-sm text-red-600">{errorMessage}</p>
            </div>

            <div className="space-y-3">
              <button
                onClick={() => navigate('/login')}
                className="w-full bg-blue-600 text-white py-3 px-6 rounded-lg font-semibold hover:bg-blue-700 transition"
              >
                Go to Login
              </button>

              <button
                onClick={() => navigate('/')}
                className="w-full bg-gray-100 text-gray-700 py-3 px-6 rounded-lg font-medium hover:bg-gray-200 transition"
              >
                Back to Home
              </button>
            </div>

            {mode === 'verifyEmail' && (
              <div className="mt-6 text-sm text-gray-600">
                <p>
                  <strong>Link already used?</strong> Try logging in - you may
                  already be verified!
                </p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default AuthActionPage;
